/* eslint-disable no-magic-numbers */
import styled, { css } from 'styled-components';
import React from 'react';
import { withCssSelector } from '../shared/withCssSelector';
import { Icon } from '../icon';
import { FormInputSize, formInputSizeDefinitions, formInputSizes } from '../utils/formInputSize';
import { transition } from '../themes/mainTheme';
import { BoxedFieldProps, boxedFieldStates } from '../shared/boxedField';

// Sizes come from the shared form scale, so a search field and a plain input
// of the same size are the same box.
export type SearchInputSize = FormInputSize;

const StyledIcon = styled(Icon)`
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	color: ${props => props.theme.text.tertiary};
	transition: ${transition('color')};
`;

const StyledInput = styled.input`
	flex: 1 1 auto;
	min-width: 0;
	appearance: none;
	background: none;
	border: 0;
	padding: 0;
	font-family: ${props => props.theme.font};
	line-height: 1.25;
	color: ${props => props.theme.text.primary};
	color-scheme: dark;

	&:focus {
		outline: 0;
	}

	&::placeholder {
		color: ${props => props.theme.text.tertiary};
	}
`;

// ponytail: read once at module load, not per render. A user does not switch
// operating systems mid-session, and this dodges an effect + re-render. Reads
// as "Ctrl" anywhere that is not an Apple platform, which is the useful split —
// ⌘ is the wrong key everywhere else.
const isApple = typeof navigator !== 'undefined'
	&& /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

const modifierKey = isApple ? '⌘' : 'Ctrl';

const StyledShortcut = styled.span`
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 24px;
	padding: 0 6px;
	border-radius: 2px;
	background: ${props => props.theme.fill.infoTertiary};
	color: ${props => props.theme.text.info};
	font-family: ${props => props.theme.font};
	font-size: 14px;
	line-height: 1.25;
	white-space: nowrap;
	user-select: none;
`;

const StyledContainer = styled.div<{ $size: SearchInputSize } & BoxedFieldProps>`
	display: flex;
	align-items: center;
	box-sizing: border-box;
	overflow: hidden;
	background: ${props => props.theme.form.input.background};

	// Stroke and every state it moves through — the same ones a plain Input draws.
	${boxedFieldStates}

	${({ $size }) => {
		const size = formInputSizeDefinitions[$size];
		return css`
			height: ${size.height}px;
			gap: ${size.gap}px;
			padding: 0 ${size.paddingHorizontal}px;

			${StyledIcon} {
				font-size: ${size.iconSize}px;
			}

			${StyledInput} {
				font-size: ${size.fontSize}px;
			}
		`;
	}}

	// The magnifier tracks the stroke: it lifts whenever the box does.
	&:hover,
	&:focus-within {
		${StyledIcon} {
			color: ${props => props.theme.fill.secondary};
		}
	}

	// Once the field has focus the shortcut is spent.
	&:focus-within ${StyledShortcut} {
		display: none;
	}
`;

export type SearchInputProps = {
	hasError?: boolean | undefined,
	/**
	 * Key that focuses the field, e.g. `K` renders a `⌘ + K` chip — `Ctrl + K`
	 * off Apple platforms — and binds that combination document-wide.
	 */
	shortcut?: string | undefined,
	size?: SearchInputSize | undefined
} & Omit<React.ComponentPropsWithoutRef<'input'>, 'size'>

export const SearchInput = withCssSelector(React.forwardRef<HTMLInputElement, SearchInputProps>(
	function SearchInput(
		{ size = formInputSizes.compact, className, disabled, hasError, shortcut, tabIndex, readOnly, ...otherProps },
		ref
	) {
		const inputRef = React.useRef<HTMLInputElement>(null);
		React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

		// Bound on the document, not the field — the whole point of the shortcut
		// is reaching a field that does not have focus yet.
		React.useEffect(() => {
			if (!shortcut || disabled) {
				return undefined;
			}

			const onKeyDown = (event: KeyboardEvent) => {
				if (!(isApple ? event.metaKey : event.ctrlKey)) {
					return;
				}
				if (event.key.toLowerCase() !== shortcut.toLowerCase()) {
					return;
				}

				event.preventDefault();
				inputRef.current?.focus();
			};

			document.addEventListener('keydown', onKeyDown);
			return () => document.removeEventListener('keydown', onKeyDown);
		}, [shortcut, disabled]);

		return <StyledContainer
			className={className}
			$size={size}
			$disabled={disabled}
			$hasError={hasError}>
			<StyledIcon icon="search" />
			<StyledInput
				placeholder="Search"
				{...otherProps}
				type="search"
				disabled={disabled}
				tabIndex={disabled ? -1 : tabIndex}
				readOnly={disabled ? true : readOnly}
				ref={inputRef} />
			{shortcut && <StyledShortcut>{modifierKey} + {shortcut}</StyledShortcut>}
		</StyledContainer>;
	}
), StyledContainer.toString());
